import React from 'react';
import PropTypes from 'prop-types';
import { CountryFlag, Divider, Grid, Paper, Stack, Typography } from '@esgian/esgianui';
import { PortMap } from '@components/Maps';
import { VesselPortCallsTable, UpcomingVesselPortCallsTable } from '@components/Tables';
import { ProfileLink } from '@components/Links';
import { useTimezone } from '@hooks/useTimezone';
import { OverviewKeyValue } from '@components';
import { getLatestPortCalls, getMaxPortDraught, getUpcomingPortCalls } from '@api';
import { DEFAULT_NUM_PORT_CALLS, GEOGRAPHICAL_TYPES } from '@constants';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getGeneralSlice } from '@store/features';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PORT_UPCOMING_TITLE } from '@constants/tooltips';

function PortOverviewSection({ portDetailsQuery = {} }) {
  const { getTimeZoneDisplay } = useTimezone();
  const { operatorType, commercialCategoryType, segment } = useSelector(getGeneralSlice);
  const params = useParams();

  const maxPortDraughtQuery = useQuery({
    queryKey: [
      'maxPortDraught',
      {
        geographicalType: GEOGRAPHICAL_TYPES.PORT,
        operatorTypeId: operatorType,
        segmentTypeId: segment.id,
        geoIds: [parseInt(params.portId)],
        commercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!params.portId,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) =>
      getMaxPortDraught(queryKey[1], signal)
        .then((result) => result.maxDraughtLastPortCalls)
        .catch((error) => {
          if (error.name !== 'AbortError') {
            toast.error('Unable to fetch max port draught.', {
              position: 'top-right'
            });
          }
          return null;
        })
  });

  const latestPortCallsQuery = useQuery({
    queryKey: [
      'latestPortCalls',
      {
        geographicalType: GEOGRAPHICAL_TYPES.PORT,
        operatorTypeId: operatorType,
        segmentTypeId: segment.id,
        geoIds: [parseInt(params.portId)],
        numberOfPortCalls: 250,
        commercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!params.portId,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) =>
      getLatestPortCalls(queryKey[1], signal)
        .then((result) => result)
        .catch((error) => {
          if (error.name !== 'AbortError') {
            toast.error('Unable to fetch port calls.', {
              position: 'top-right'
            });
          }
          return null;
        })
  });

  const upcomingPortCallsQuery = useQuery({
    queryKey: [
      'upcomingPortCalls',
      {
        geoIds: [parseInt(params.portId)],
        geoType: GEOGRAPHICAL_TYPES.PORT,
        numberOfIncomingEntries: DEFAULT_NUM_PORT_CALLS,
        segmentTypeId: segment.id,
        operatorTypeId: operatorType,
        commercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!params.portId,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) =>
      getUpcomingPortCalls(queryKey[1], signal)
        .then((result) => result)
        .catch((error) => {
          if (error.name !== 'AbortError') {
            toast.error('Unable to fetch upcoming port calls.', {
              position: 'top-right'
            });
          }
          return null;
        })
  });

  const { portName, country, isoAlpha2, cpointlat, cpointlon, unlocode, id } =
    portDetailsQuery.data || {};
  const { vesselName, draught, draughtDate, imo } = maxPortDraughtQuery.data || {};

  return (
    <Grid container spacing={2}>
      <Grid item xs={4}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Grid container spacing={2}>
            <Grid item container spacing={2} xs={12}>
              <Grid item alignSelf={'center'}>
                <Typography variant={'h6'}>Overview</Typography>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <OverviewKeyValue
                  title={'Name:'}
                  value={portName}
                  loading={portDetailsQuery.isFetching}
                />
                <OverviewKeyValue
                  title={'Country:'}
                  value={
                    <Stack direction={'row'} spacing={1} alignItems={'center'}>
                      <CountryFlag size={15} countryCode={isoAlpha2} />
                      <Typography variant={'body2'}>{country?.name}</Typography>
                    </Stack>
                  }
                  loading={portDetailsQuery.isFetching}
                />
                <OverviewKeyValue
                  title={'Location(lat/lon):'}
                  value={`${cpointlat}/${cpointlon}`}
                  loading={portDetailsQuery.isFetching}
                />
                <OverviewKeyValue
                  title={'UNLOCODE:'}
                  value={`${unlocode}`}
                  loading={portDetailsQuery.isFetching}
                />
                <OverviewKeyValue
                  title={'Max reported Draught:'}
                  value={
                    <Stack>
                      <ProfileLink
                        linkVariant={'body2'}
                        profile={'vessel'}
                        name={vesselName}
                        id={imo}
                      />
                      <Typography variant={'body2'}>
                        {`${draught}m on ${getTimeZoneDisplay(draughtDate)}`}
                      </Typography>
                    </Stack>
                  }
                  loading={portDetailsQuery.isFetching}
                />
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={8}>
        <PortMap
          height={'40vh'}
          loading={portDetailsQuery.isFetching}
          portData={portDetailsQuery.data}
        />
      </Grid>
      <Grid item xs={12}>
        <VesselPortCallsTable
          geographicalType={GEOGRAPHICAL_TYPES.PORT}
          hideColumns={['portName', 'portCode', 'countryName']}
          title={'Most Recent Vessels Port Calls'}
          portCalls={latestPortCallsQuery.data}
          loading={latestPortCallsQuery.isFetching}
          name={`${portName}-port-calls`}
          geoId={id}
          labels={[
            'Vessel',
            'IMO',
            'Operator',
            'Port',
            'UNLOCODE',
            'Arrival',
            'Departure',
            'Port Time (hours)'
          ]}
          headers={[
            'vesselName',
            'imo',
            'operatorShortName',
            'portName',
            'portCode',
            'startDate',
            'endDate',
            'timeInPort'
          ]}
          allowDownloadAll={true}
        />
      </Grid>
      <Grid item xs={12}>
        <UpcomingVesselPortCallsTable
          assetLevel={'port'}
          title={'Upcoming Vessels Port Calls'}
          upcomingPortCalls={upcomingPortCallsQuery.data}
          loading={upcomingPortCallsQuery.isFetching}
          name={portName}
          titleWithTooltip
          tooltipText={PORT_UPCOMING_TITLE}
        />
      </Grid>
    </Grid>
  );
}

PortOverviewSection.propTypes = {
  portDetailsQuery: PropTypes.object
};

export default PortOverviewSection;
